import { useEffect, useMemo, useState } from "react";
import { Printer, DownloadSimple, QrCode } from "@phosphor-icons/react";

import { api } from "@/lib/api";

/**
 * VehicleQrCard — displays the QR code for a vehicle plus print / download
 * shortcuts. Drivers scan this from the vehicle's dashboard to auto-fill the
 * operation form.
 */
export default function VehicleQrCard({ vehicleId, plate, companyName }) {
    const [svgText, setSvgText] = useState("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!vehicleId) return;
        let cancelled = false;
        setLoading(true);
        setErr("");
        (async () => {
            try {
                const res = await api.get(`/vehicles/${vehicleId}/qr-code.svg`, {
                    responseType: "text",
                });
                if (!cancelled) setSvgText(res.data);
            } catch (e) {
                if (!cancelled) setErr("QRコードを取得できません");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [vehicleId]);

    const dataUri = useMemo(
        () => (svgText ? `data:image/svg+xml;utf8,${encodeURIComponent(svgText)}` : ""),
        [svgText],
    );

    const handlePrint = () => {
        const w = window.open("", "_blank", "width=600,height=800");
        if (!w) return;
        w.document.write(`<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8" />
<title>車両QR - ${plate || ""}</title>
<style>
  body{font-family:sans-serif;text-align:center;padding:24px;margin:0}
  .plate{font-size:32px;font-weight:900;letter-spacing:2px;margin-bottom:12px}
  .company{font-size:14px;color:#555;margin-bottom:24px}
  .qr{width:60vmin;height:60vmin;max-width:520px;max-height:520px;margin:0 auto}
  .qr svg{width:100%;height:100%;display:block}
  .foot{margin-top:24px;font-size:11px;color:#777;letter-spacing:2px;text-transform:uppercase}
  @media print{.no-print{display:none}}
</style></head><body>
<div class="plate">${plate || ""}</div>
<div class="company">${companyName || ""}</div>
<div class="qr">${svgText}</div>
<div class="foot">PRC CMC · 乗車開始画面で読み取ってください</div>
<script>window.onload=function(){setTimeout(function(){window.print()},400)};<\/script>
</body></html>`);
        w.document.close();
    };

    const handleDownload = async () => {
        try {
            const res = await api.get(`/vehicles/${vehicleId}/qr-code.png`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `qr_${plate || vehicleId}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            // silent
        }
    };

    return (
        <div className="prc-card p-5" data-testid="vd-qr-card">
            <div className="flex items-center justify-between mb-3 gap-3">
                <p className="prc-label-overline inline-flex items-center gap-2"><QrCode size={16} />車両QRコード</p>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={handlePrint} disabled={!svgText}
                        className="prc-btn-ghost inline-flex items-center gap-2 text-xs disabled:opacity-40"
                        data-testid="vd-qr-print">
                        <Printer size={16} />印刷
                    </button>
                    <button type="button" onClick={handleDownload} disabled={!svgText}
                        className="prc-btn-ghost inline-flex items-center gap-2 text-xs disabled:opacity-40"
                        data-testid="vd-qr-download">
                        <DownloadSimple size={16} />PNG
                    </button>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-40 h-40 bg-white p-2 rounded-sm border border-[var(--prc-border)] shrink-0" data-testid="vd-qr-image-wrap">
                    {loading && <p className="text-xs text-[var(--prc-muted)] text-center pt-16">読込中...</p>}
                    {err && <p className="text-xs text-[var(--prc-red)] text-center pt-16">{err}</p>}
                    {!loading && !err && dataUri && (
                        <img src={dataUri} alt={`QR ${plate || ""}`} className="w-full h-full" />
                    )}
                </div>
                <div className="text-sm text-[var(--prc-muted)] flex-1">
                    <p className="mb-2">このQRを車両のダッシュボードに貼り付けると、スタッフは<b className="text-[var(--prc-ink-2)]">乗車開始画面のQRスキャン</b>ボタンから瞬時に車両を選択できます。</p>
                    <p className="text-xs">
                        コード内容: <span className="font-mono">PRC-VEH:{vehicleId}</span>
                    </p>
                </div>
            </div>
        </div>
    );
}
